import { Container, Sprite, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';
import { waitForMs } from 'utils/async-utils';
import { triggerParticleFX } from 'libs/particles/triggerParticleFX';
import { HammerAnimationLayout } from '../../../../layouts/boosters/HammerAnimationLayout';
import type { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import { soundManager } from '../../../app/SoundManager';

export class HammerAnimation extends Container {
  public onImpact = new Signal<() => void>();

  private readonly animationDelay = 100;

  private readonly hammerAnimationDuration = 880;

  private readonly hammerMovementSfxDelay = 250;

  private readonly hammerHitDelay = 700;

  private readonly layout = new HammerAnimationLayout();

  constructor(
    private readonly particleLayer: FXParticleLayer,
    private readonly hitBoard: () => Promise<void>,
  ) {
    super();
    this.layout.root.props({
      parent: this,
    });
    this.layout.hammerSprite.texture = Texture.from('Hammer_Animation_Frame_1');
    this.layout.hammerSprite.visible = false;
  }

  public async play(): Promise<void> {
    await Promise.all([
      this.animateFrames(),
      this.playHammerMovementSfx(),
      this.animateImpact(),
      this.animateHitBoard(),
      this.animateParticles(),
    ]);
    this.onImpact.emit();
  }

  private async animateHitBoard(): Promise<void> {
    const globalPos = this.layout.hammerSprite.getGlobalPosition();
    const localPos = this.particleLayer.toLocal(globalPos);

    await waitForMs(this.hammerHitDelay);
    void triggerParticleFX('Hammer_Impact_Particles', this.particleLayer, localPos.x, localPos.y);
    await this.hitBoard();
  }

  private async animateImpact(): Promise<void> {
    if (this.destroyed) return;

    const impactSprite = new Sprite(Texture.from('Hammer_Impact')).props({
      parent: this.layout.impactContainer,
      anchor: 0.5,
      scale: 0,
    });

    impactSprite.label = 'Hammer_Impact';

    await waitForMs(this.hammerHitDelay);
    soundManager.play('Hammer_Hit');

    await impactSprite.tweenTo({ scaleX: 1, scaleY: 1 }, 200, 'cubicOut', true).asyncOnComplete();
    await impactSprite.tweenTo({ alpha: 0 }, 100, 'cubicOut', true).asyncOnComplete();
  }

  private async animateFrames(): Promise<void> {
    const { root, hammerSprite } = this.layout;

    const frames = [
      { texture: 'Hammer_Animation_Frame_1' },
      { texture: 'Hammer_Animation_Frame_2' },
      { texture: 'Hammer_Animation_Frame_3' },
      { texture: 'Hammer_Animation_Frame_4_5' },
      { texture: 'Hammer_Animation_Frame_4_5' },
      // { texture: 'Hammer_Animation_Frame_4_5' }, // skipping frame 6
      { texture: 'Hammer_Animation_Frame_7' },
      { texture: 'Hammer_Animation_Frame_8' },
      { texture: 'Hammer_Animation_Frame_9' },
      { texture: 'Hammer_Animation_Frame_10' },
      { texture: 'Hammer_Animation_Frame_11' },
      { texture: 'Hammer_Animation_Frame_12' },
      { texture: 'Hammer_Animation_Frame_13' },
      { texture: 'Hammer_Animation_Frame_14_15_16_17' },
      { texture: 'Hammer_Animation_Frame_14_15_16_17' },
      { texture: 'Hammer_Animation_Frame_14_15_16_17' },
      { texture: 'Hammer_Animation_Frame_14_15_16_17' },
      { texture: 'Hammer_Animation_Frame_18' },
      { texture: 'Hammer_Animation_Frame_19' },
      { texture: 'Hammer_Animation_Frame_20' },
      { texture: 'Hammer_Animation_Frame_21' },
      { texture: 'Hammer_Animation_Frame_22_23' },
      { texture: 'Hammer_Animation_Frame_22_23' },
    ];

    await waitForMs(this.animationDelay);

    if (root.destroyed) return;

    this.layout.hammerSprite.alpha = 0;
    this.layout.hammerSprite.visible = true;
    await this.layout.hammerSprite.tweenTo({ alpha: 1 }, 100, 'sinOut', true).asyncOnComplete();

    const frameDuration = this.hammerAnimationDuration / frames.length;

    let promise = Promise.resolve();
    frames.forEach((frame) => {
      promise = promise.then(async () => {
        if (root.destroyed) return;
        hammerSprite.props(frame);
        await waitForMs(frameDuration);
      });
    });

    await promise;

    await hammerSprite.tweenTo({ alpha: 0 }, 100, 'sinOut', true).asyncOnComplete();
    this.layout.hammerSprite.visible = false;
  }

  private async animateParticles(): Promise<void> {
    const globalPos = this.layout.hammerSprite.getGlobalPosition();
    const localPos = this.particleLayer.toLocal(globalPos);

    await waitForMs(this.hammerAnimationDuration);
    void triggerParticleFX('Hammer_Hit_Particles', this.particleLayer, localPos.x, localPos.y);
  }

  private async playHammerMovementSfx(): Promise<void> {
    await waitForMs(this.hammerMovementSfxDelay);
    soundManager.play('Hammer_Movement');
  }
}
